/**
 * PersonnelAgentRecap — Récap par agent des bons personnel du mois.
 *
 * Regroupe les bons par `employee_name` et affiche pour chacun UNE bulle globale :
 * "Total dû = À retenir sur salaire (authorized + settled) + Non autorisé (cancelled)".
 * Clic sur la bulle → modale détaillée avec breakdown + liste chronologique.
 *
 * 🆕 Fév 2026 : validation CAS PAR CAS par l'admin depuis la modale détail —
 * boutons "Autoriser (prix normal)", "Autoriser avec remise…", "Refuser" sur
 * chaque bon en attente.
 *
 * Utilisé dans ManagerOrdersTab et EmployeeOrdersTab.
 */
import React, { useMemo, useState } from "react";
import axios from "axios";
import { Users, X, Calendar, ChefHat, CheckCircle, Clock, AlertTriangle, ThumbsUp, Percent, Ban } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_META = {
  authorized: { label: "Autorisé", color: "emerald", Icon: CheckCircle },
  settled: { label: "Réglé sur salaire", color: "slate", Icon: CheckCircle },
  pending_manager: { label: "En attente Resp. Op.", color: "amber", Icon: Clock },
  pending_director: { label: "En attente Direction", color: "blue", Icon: Clock },
  cancelled: { label: "NON AUTORISÉ", color: "red", Icon: AlertTriangle },
};

const PersonnelAgentRecap = ({
  orders = [],
  accent = "violet",
  formatPrice,
  dataTestId = "personnel-agent-recap",
  scope = "employee",           // 🆕 "employee" | "manager"
  isAdmin = false,              // 🆕 pour afficher les boutons d'action
  adminName = "Admin",          // 🆕 trace audit
  onRefresh = null,             // 🆕 callback pour rafraîchir la liste après action
}) => {
  const fmt = typeof formatPrice === "function" ? formatPrice : (v) => Number(v || 0).toLocaleString("fr-FR");
  const [selectedAgent, setSelectedAgent] = useState(null);

  // 🆕 États d'action cas par cas
  const [busyOrderId, setBusyOrderId] = useState(null);
  const [discountOrder, setDiscountOrder] = useState(null);         // bon en cours de remise
  const [discountRate, setDiscountRate] = useState(50);              // slider 0-100
  const [discountReason, setDiscountReason] = useState("");
  const [refuseOrder, setRefuseOrder] = useState(null);              // bon en cours de refus
  const [refuseReason, setRefuseReason] = useState("");

  const byAgent = useMemo(() => {
    const map = new Map();
    for (const o of orders || []) {
      const key = o.employee_name || "—";
      if (!map.has(key)) {
        map.set(key, {
          name: key,
          position: o.employee_position || "",
          authorized: 0,
          settled: 0,
          pending: 0,
          not_authorized: 0,
          count: 0,
          orders: [],
        });
      }
      const bucket = map.get(key);
      bucket.count += 1;
      bucket.orders.push(o);
      const status = o.status;
      const total = Number(o.total || 0);
      const subtotal = Number(o.subtotal || 0);
      if (status === "authorized") bucket.authorized += total;
      else if (status === "settled") bucket.settled += total;
      else if (status === "pending_manager" || status === "pending_director") bucket.pending += subtotal;
      else if (status === "cancelled") bucket.not_authorized += subtotal;
    }
    return Array.from(map.values())
      .map((b) => ({ ...b, total_due: b.authorized + b.settled + b.not_authorized }))
      .sort((a, b) => b.total_due - a.total_due);
  }, [orders]);

  if (byAgent.length === 0) return null;

  const accentMap = {
    violet: {
      ring: "border-violet-500/40 hover:border-violet-500/70",
      bubble: "bg-gradient-to-br from-violet-600 to-violet-800 border-violet-400/50",
      text: "text-violet-200",
      title: "text-violet-300",
    },
    pink: {
      ring: "border-pink-500/40 hover:border-pink-500/70",
      bubble: "bg-gradient-to-br from-pink-600 to-pink-800 border-pink-400/50",
      text: "text-pink-200",
      title: "text-pink-300",
    },
  };
  const c = accentMap[accent] || accentMap.violet;

  // === Actions admin cas par cas ===
  const authorizeAtRate = async (order, rate, reason = "") => {
    if (!order) return;
    setBusyOrderId(order.id);
    try {
      await axios.post(`${API}/admin/personnel-orders/authorize-single`, {
        scope,
        order_id: order.id,
        discount_rate: rate,
        custom_reason: reason,
        admin_name: adminName,
      });
      const pctLabel = rate === 0 ? "prix normal" : `remise ${Math.round(rate * 100)}%`;
      toast.success(`Bon autorisé (${pctLabel})`);
      setDiscountOrder(null); setDiscountReason(""); setDiscountRate(50);
      if (onRefresh) await onRefresh();
      setSelectedAgent(null); // ferme la modale pour reload propre
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur autorisation");
    } finally {
      setBusyOrderId(null);
    }
  };

  const refuseOrderNow = async () => {
    if (!refuseOrder) return;
    const reason = (refuseReason || "").trim();
    if (reason.length < 3) { toast.error("Motif obligatoire (min. 3 caractères)"); return; }
    setBusyOrderId(refuseOrder.id);
    try {
      const endpoint = scope === "manager" ? "manager-orders" : "employee-orders";
      await axios.delete(`${API}/${endpoint}/${refuseOrder.id}`, {
        params: { reason, by_role: "admin" },
      });
      toast.success("Bon refusé");
      setRefuseOrder(null); setRefuseReason("");
      if (onRefresh) await onRefresh();
      setSelectedAgent(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur refus");
    } finally {
      setBusyOrderId(null);
    }
  };

  return (
    <div className="mb-3" data-testid={dataTestId}>
      <div className="flex items-center gap-2 mb-2">
        <Users className={`w-4 h-4 ${c.title}`} strokeWidth={2.4} />
        <h4 className={`text-[13px] font-bold ${c.title}`}>
          Récap par agent <span className="text-slate-500 font-normal text-[11.5px]">— {byAgent.length} agent{byAgent.length > 1 ? "s" : ""}</span>
        </h4>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {byAgent.map((a) => {
          const hasPending = a.orders.some(o => o.status === "pending_manager" || o.status === "pending_director");
          const pendingCount = a.orders.filter(o => o.status === "pending_manager" || o.status === "pending_director").length;
          return (
          <button
            key={a.name}
            type="button"
            onClick={() => setSelectedAgent(a)}
            className={`text-left rounded-xl border bg-slate-800/40 ${c.ring} p-2.5 transition-all hover:bg-slate-800/70 hover:shadow-lg hover:-translate-y-0.5 cursor-pointer relative`}
            data-testid={`recap-agent-${a.name}`}
            title="Cliquer pour voir le détail date par date"
          >
            {/* 🆕 Dot d'alerte quand l'agent a des bons en attente de décision */}
            {hasPending && (
              <span
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 border-2 border-slate-900 text-slate-900 text-[10px] font-black flex items-center justify-center shadow-lg animate-pulse"
                title={`${pendingCount} bon${pendingCount > 1 ? "s" : ""} en attente`}
                data-testid={`recap-agent-alert-${a.name}`}
              >
                {pendingCount}
              </span>
            )}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-white font-bold text-[13px] truncate leading-tight">{a.name}</p>
                <p className="text-slate-500 text-[10.5px] truncate">{a.position || "—"} · {a.count} bon{a.count > 1 ? "s" : ""}</p>
              </div>
              <div className={`${c.bubble} rounded-full px-3 py-1.5 border shadow-md flex-shrink-0`}>
                <p className="text-white font-black text-[14px] tabular-nums leading-none whitespace-nowrap">{fmt(a.total_due)}<span className="text-white/70 text-[10px] ml-0.5">F</span></p>
              </div>
            </div>
          </button>
          );
        })}
      </div>

      {/* Modale détail agent avec actions cas par cas */}
      <Dialog open={!!selectedAgent} onOpenChange={(v) => { if (!v) setSelectedAgent(null); }}>
        <DialogContent
          className={`bg-slate-900 border ${accent === "pink" ? "border-pink-500/50" : "border-violet-500/50"} text-white rounded-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6`}
          style={{ width: "min(calc(100vw - 1.5rem), 44rem)" }}
          data-testid="agent-detail-modal"
        >
          {selectedAgent && (() => {
            const sorted = [...(selectedAgent.orders || [])].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
            return (
              <>
                <DialogHeader>
                  <DialogTitle className={`${c.title} flex items-center gap-2 text-lg`}>
                    <Users className="w-5 h-5" />
                    {selectedAgent.name}
                    <span className={`ml-auto ${c.bubble} rounded-full px-3 py-1 border text-white text-[13px] font-black tabular-nums`}>
                      {fmt(selectedAgent.total_due)} F
                    </span>
                  </DialogTitle>
                  <p className="text-slate-400 text-[12px]">{selectedAgent.position || "—"} · {selectedAgent.count} bon{selectedAgent.count > 1 ? "s" : ""}</p>
                </DialogHeader>

                <div className="grid grid-cols-3 gap-2 mt-3">
                  <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-2 text-center">
                    <p className="text-emerald-200 text-[9.5px] uppercase font-bold">À retenir</p>
                    <p className="text-emerald-100 text-[15px] font-black tabular-nums">{fmt(selectedAgent.authorized + selectedAgent.settled)} F</p>
                  </div>
                  <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-2 text-center">
                    <p className="text-red-200 text-[9.5px] uppercase font-bold">Non autorisé</p>
                    <p className="text-red-100 text-[15px] font-black tabular-nums">{fmt(selectedAgent.not_authorized)} F</p>
                  </div>
                  <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-2 text-center">
                    <p className="text-amber-200 text-[9.5px] uppercase font-bold">En attente</p>
                    <p className="text-amber-100 text-[15px] font-black tabular-nums">{fmt(selectedAgent.pending)} F</p>
                  </div>
                </div>

                <div className="mt-4 space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="agent-detail-list">
                  {sorted.map((o) => {
                    const meta = STATUS_META[o.status] || { label: o.status, color: "slate", Icon: Clock };
                    const Icn = meta.Icon;
                    const isCancelled = o.status === "cancelled";
                    const isPending = o.status === "pending_manager" || o.status === "pending_director";
                    const shownAmount = (isCancelled || isPending) ? o.subtotal : o.total;
                    const dateStr = (o.created_at || "").slice(0, 10);
                    const timeStr = (o.created_at || "").slice(11, 16);
                    const items = (o.items || []).map((it) => `${it.quantity}× ${it.name}`).join(" · ");
                    const busy = busyOrderId === o.id;
                    return (
                      <div
                        key={o.id}
                        className={`rounded-lg border p-2.5 ${
                          isCancelled ? "bg-red-950/25 border-red-500/40"
                          : isPending ? "bg-amber-950/20 border-amber-500/30"
                          : "bg-slate-800/60 border-slate-700"
                        }`}
                        data-testid={`agent-detail-order-${o.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 text-[11.5px] mb-0.5 flex-wrap">
                              <Calendar className="w-3 h-3 text-slate-500" />
                              <span className="text-slate-300 font-semibold tabular-nums">{dateStr}</span>
                              <span className="text-slate-600">·</span>
                              <span className="text-slate-500 tabular-nums">{timeStr}</span>
                              <span className={`ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9.5px] font-black uppercase tracking-wide bg-${meta.color}-500/20 text-${meta.color}-300 border border-${meta.color}-500/40`}>
                                <Icn className="w-2.5 h-2.5" /> {meta.label}
                              </span>
                            </div>
                            <p className="text-white text-[12px] flex items-start gap-1.5">
                              <ChefHat className="w-3 h-3 text-slate-500 flex-shrink-0 mt-0.5" />
                              <span className="line-clamp-2">{items || "—"}</span>
                            </p>
                            {isCancelled && o.cancellation_reason && (
                              <p className="text-red-300 text-[10.5px] mt-0.5 italic">Motif : {o.cancellation_reason}</p>
                            )}
                            {o.regularization_reason && (
                              <p className="text-blue-300 text-[10.5px] mt-0.5 italic">Régularisé : {o.regularization_reason}</p>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className={`font-black text-[15px] tabular-nums ${
                              isCancelled ? "text-red-200"
                              : isPending ? "text-amber-200"
                              : "text-emerald-200"
                            }`}>
                              {fmt(shownAmount)} F
                            </p>
                            <p className="text-slate-500 text-[9.5px]">
                              {isCancelled ? "dû" : isPending ? "prix normal" : "à retenir"}
                            </p>
                          </div>
                        </div>

                        {/* 🆕 Actions admin cas par cas — visibles pour bons en attente ET annulés (ré-autorisation) */}
                        {isAdmin && (isPending || isCancelled) && (
                          <div className="mt-2 pt-2 border-t border-amber-500/20 flex flex-wrap gap-1.5" data-testid={`agent-detail-actions-${o.id}`}>
                            <Button
                              size="sm"
                              disabled={busy}
                              onClick={() => authorizeAtRate(o, 0)}
                              className="h-7 px-2 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                              data-testid={`authorize-full-${o.id}`}
                              title={isCancelled ? "Ré-autoriser au prix normal" : "Autoriser au prix normal (aucune remise)"}
                            >
                              <ThumbsUp className="w-3 h-3 mr-1" /> {isCancelled ? "Ré-autoriser" : "Autoriser"} (prix normal)
                            </Button>
                            <Button
                              size="sm"
                              disabled={busy}
                              onClick={() => { setDiscountOrder(o); setDiscountRate(50); setDiscountReason(""); }}
                              className="h-7 px-2 text-[11px] bg-blue-600 hover:bg-blue-700 text-white"
                              data-testid={`authorize-discount-${o.id}`}
                              title="Autoriser avec un taux de remise personnalisé (0-100%)"
                            >
                              <Percent className="w-3 h-3 mr-1" /> Avec remise…
                            </Button>
                            {!isCancelled && (
                              <Button
                                size="sm"
                                disabled={busy}
                                onClick={() => { setRefuseOrder(o); setRefuseReason(""); }}
                                className="h-7 px-2 text-[11px] bg-red-600 hover:bg-red-700 text-white"
                                data-testid={`refuse-single-${o.id}`}
                                title="Refuser ce bon (motif obligatoire)"
                              >
                                <Ban className="w-3 h-3 mr-1" /> Refuser
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="mt-3 flex justify-end">
                  <Button
                    variant="outline"
                    onClick={() => setSelectedAgent(null)}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700 h-9"
                    data-testid="agent-detail-close"
                  >
                    <X className="w-4 h-4 mr-1.5" /> Fermer
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Modale REMISE personnalisée */}
      <Dialog open={!!discountOrder} onOpenChange={(v) => { if (!v) { setDiscountOrder(null); setDiscountReason(""); } }}>
        <DialogContent
          className="bg-slate-900 border-blue-500/50 text-white rounded-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6"
          style={{ width: "min(calc(100vw - 1.5rem), 32rem)" }}
          data-testid="single-discount-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-blue-300 flex items-center gap-2 text-lg">
              <Percent className="w-5 h-5" />
              Autoriser avec remise
            </DialogTitle>
          </DialogHeader>
          {discountOrder && (() => {
            const sub = Number(discountOrder.subtotal || 0);
            const disc = Math.round(sub * (discountRate / 100));
            const finalT = sub - disc;
            return (
              <div className="space-y-3 py-2">
                <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-3 text-[12px]">
                  <p className="text-slate-300"><b>{discountOrder.employee_name}</b> · {(discountOrder.items || []).map(i => `${i.quantity}× ${i.name}`).join(" · ") || "—"}</p>
                  <p className="text-slate-500 mt-1">Prix normal : <span className="text-white font-bold tabular-nums">{fmt(sub)} F</span></p>
                </div>
                <div>
                  <label className="text-slate-300 text-xs font-semibold uppercase tracking-wider">
                    Taux de remise : <span className="text-blue-300 font-black">{discountRate}%</span>
                  </label>
                  <input
                    type="range" min="0" max="100" step="5"
                    value={discountRate}
                    onChange={(e) => setDiscountRate(parseInt(e.target.value, 10))}
                    className="w-full mt-2 accent-blue-500"
                    data-testid="single-discount-slider"
                  />
                  <div className="flex justify-between text-[10px] text-slate-500 mt-0.5">
                    <span>0% (prix normal)</span>
                    <span>50%</span>
                    <span>100% (gratuit)</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center text-[12px]">
                  <div className="rounded bg-slate-800/50 border border-slate-700 p-2">
                    <p className="text-slate-500 text-[10px] uppercase">Prix</p>
                    <p className="text-white font-bold tabular-nums">{fmt(sub)} F</p>
                  </div>
                  <div className="rounded bg-blue-500/10 border border-blue-500/30 p-2">
                    <p className="text-blue-300 text-[10px] uppercase">Remise</p>
                    <p className="text-blue-200 font-bold tabular-nums">-{fmt(disc)} F</p>
                  </div>
                  <div className="rounded bg-emerald-500/10 border border-emerald-500/30 p-2">
                    <p className="text-emerald-300 text-[10px] uppercase">À retenir</p>
                    <p className="text-emerald-200 font-black tabular-nums">{fmt(finalT)} F</p>
                  </div>
                </div>
                <div>
                  <label className="text-slate-300 text-xs">Motif (optionnel, archivé)</label>
                  <input
                    type="text"
                    value={discountReason}
                    onChange={(e) => setDiscountReason(e.target.value)}
                    placeholder={`Ex : remise exceptionnelle ${discountRate}% validée par la direction`}
                    className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-white text-sm placeholder-slate-500 focus:border-blue-500/60 focus:outline-none"
                    data-testid="single-discount-reason"
                  />
                </div>
                <div className="flex gap-2 justify-end pt-2 border-t border-slate-700">
                  <Button variant="outline" className="border-slate-600 text-slate-300" onClick={() => { setDiscountOrder(null); setDiscountReason(""); }} data-testid="single-discount-cancel">
                    Annuler
                  </Button>
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold"
                    onClick={() => authorizeAtRate(discountOrder, discountRate / 100, discountReason)}
                    disabled={busyOrderId === discountOrder.id}
                    data-testid="single-discount-confirm"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> Autoriser à {fmt(finalT)} F
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Modale REFUS avec motif obligatoire */}
      <Dialog open={!!refuseOrder} onOpenChange={(v) => { if (!v) { setRefuseOrder(null); setRefuseReason(""); } }}>
        <DialogContent
          className="bg-slate-900 border-red-500/50 text-white rounded-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6"
          style={{ width: "min(calc(100vw - 1.5rem), 32rem)" }}
          data-testid="single-refuse-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-red-300 flex items-center gap-2 text-lg">
              <Ban className="w-5 h-5" />
              Refuser ce bon
            </DialogTitle>
          </DialogHeader>
          {refuseOrder && (
            <div className="space-y-3 py-2">
              <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-3 text-[12px]">
                <p className="text-slate-300"><b>{refuseOrder.employee_name}</b> · {(refuseOrder.items || []).map(i => `${i.quantity}× ${i.name}`).join(" · ") || "—"}</p>
                <p className="text-slate-500 mt-1">Prix normal dû : <span className="text-red-300 font-bold tabular-nums">{fmt(Number(refuseOrder.subtotal || 0))} F</span></p>
              </div>
              <div>
                <label className="text-slate-300 text-xs font-semibold uppercase tracking-wider">
                  Motif de refus <span className="text-red-400">*</span>
                  <span className="text-slate-500 normal-case ml-1">(min. 3 caractères, archivé)</span>
                </label>
                <textarea
                  value={refuseReason}
                  onChange={(e) => setRefuseReason(e.target.value)}
                  placeholder="Ex : dépassement du plafond mensuel, hors périmètre de la remise personnel, etc."
                  rows={3}
                  className="mt-1 w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-white text-sm placeholder-slate-500 focus:border-red-500/60 focus:outline-none"
                  data-testid="single-refuse-reason"
                />
              </div>
              <div className="flex gap-2 justify-end pt-2 border-t border-slate-700">
                <Button variant="outline" className="border-slate-600 text-slate-300" onClick={() => { setRefuseOrder(null); setRefuseReason(""); }} data-testid="single-refuse-cancel">
                  Annuler
                </Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white font-bold"
                  onClick={refuseOrderNow}
                  disabled={busyOrderId === refuseOrder.id || refuseReason.trim().length < 3}
                  data-testid="single-refuse-confirm"
                >
                  <Ban className="w-4 h-4 mr-1" /> Confirmer le refus
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PersonnelAgentRecap;
